import React, { createContext, useContext, useState, useEffect } from 'react';
import selectionStorage from '../utils/selectionStorage';
import { authService } from '../services/authService';
import invitationService from '../services/invitationService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState(null);
  const [passwordChallenge, setPasswordChallenge] = useState(null);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [cognitoMfaChallenge, setCognitoMfaChallenge] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);

  useEffect(() => {
    checkUser();
  }, []);

  // Proactively refresh tokens every 45 minutes (Cognito tokens expire after 1 hour)
  useEffect(() => {
    const interval = setInterval(async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return;
      try {
        const refreshResponse = await authService.refreshToken(refreshToken);
        localStorage.setItem('idToken', refreshResponse.idToken);
        localStorage.setItem('accessToken', refreshResponse.accessToken);
        setIdToken(refreshResponse.idToken);
      } catch (error) {
        console.error('Proactive token refresh failed:', error);
        clearAuth();
      }
    }, 45 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const checkUser = async () => {
    const storedIdToken = localStorage.getItem('idToken');

    if (storedIdToken) {
      try {
        let activeToken = storedIdToken;

        // Proactively refresh if the token is already expired — avoids 401s on downstream calls
        if (isTokenExpired(storedIdToken)) {
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            clearAuth();
            setLoading(false);
            return;
          }
          try {
            const refreshResponse = await authService.refreshToken(refreshToken);
            activeToken = refreshResponse.idToken;
            localStorage.setItem('idToken', activeToken);
            localStorage.setItem('accessToken', refreshResponse.accessToken);
            setIdToken(activeToken);
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            clearAuth();
            setLoading(false);
            return;
          }
        }

        // Validate session with server
        const response = await authService.getSession(activeToken);

        if (response.isAuthenticated) {
          setUser(response.user);
          setIdToken(activeToken);
          await checkPendingInvitations();

        } else {
          // Session invalid, try to refresh
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              const refreshResponse = await authService.refreshToken(refreshToken);

              // Update tokens
              localStorage.setItem('idToken', refreshResponse.idToken);
              localStorage.setItem('accessToken', refreshResponse.accessToken);
              setIdToken(refreshResponse.idToken);

              // Get updated user info
              const userResponse = await authService.getSession(refreshResponse.idToken);
              setUser(userResponse.user);
              await checkPendingInvitations();
              console.log('Tokens refreshed, user loaded:', userResponse.user);
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              clearAuth();
            }
          } else {
            clearAuth();
          }
        }
      } catch (error) {
        console.error('Session check failed:', error);
        clearAuth();
      }
    }

    setLoading(false);
  };

  const clearAuth = () => {
    setUser(null);
    setIdToken(null);
    setPasswordChallenge(null);
    setMfaChallenge(null);
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // Drop the selected partner/account/hotel context so it never leaks to the
    // next user who logs in on this browser.
    // Clears THIS TAB and the seed other tabs would start from. Other open tabs keep their own
    // selection until they notice the logout, which is the same as before — the context is not what
    // authorises them, the token is, and that is removed above.
    selectionStorage.clear();
    selectionStorage.setOwnerSub(null);
  };

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);

      // Check if password change is required
      if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('New password required for user:', username);
        setPasswordChallenge({
          username: username,
          session: response.session
        });
        return {
          requiresPasswordChange: true,
          session: response.session,
          username: username
        };
      }

      // Check if MFA is required (app-level)
      if (response.challengeName === 'MFA_REQUIRED') {
        console.log('MFA required for user:', username);
        setMfaChallenge({ mfaSessionId: response.mfaSessionId });
        return { requiresMfa: true };
      }

      // Cognito EMAIL_OTP challenge — Cognito has emailed a code directly
      if (response.challengeName === 'EMAIL_OTP') {
        console.log('Cognito EMAIL_OTP challenge for user:', username);
        setCognitoMfaChallenge({ username: response.username, session: response.session });
        return { requiresCognitoMfa: true };
      }

      // Successful login - store tokens
      localStorage.setItem('idToken', response.idToken);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));

      setUser(response.user);
      setIdToken(response.idToken);
      setPasswordChallenge(null);

      const pendingInvitations = await checkPendingInvitations();
      console.log('User logged in successfully:', response.user);
      return { ...response.user, pendingInvitations };
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const completeNewPassword = async (newPassword) => {
    if (!passwordChallenge) {
      throw new Error('No password challenge in progress');
    }

    try {
      const response = await authService.setPassword(
        passwordChallenge.username,
        newPassword,
        passwordChallenge.session
      );

      // MFA required after password set
      if (response.challengeName === 'MFA_REQUIRED') {
        setPasswordChallenge(null);
        setMfaChallenge({ mfaSessionId: response.mfaSessionId });
        return { requiresMfa: true };
      }

      // Store tokens
      localStorage.setItem('idToken', response.idToken);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));

      setUser(response.user);
      setIdToken(response.idToken);
      setPasswordChallenge(null);

      const pendingInvitations = await checkPendingInvitations();
      console.log('Password set successfully, user logged in:', response.user);
      return { ...response.user, pendingInvitations };
    } catch (error) {
      console.error('Password set failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // The server reads the caller from the token, so there is nothing to pass and nothing to check
      // here first — a stored token is all a logout needs.
      if (localStorage.getItem('idToken')) {
        await authService.logout();
      }
    } catch (error) {
      console.error('Server logout error (proceeding with local logout):', error);
    }

    // Always clear local auth state
    clearAuth();
  };

  const checkPendingInvitations = async () => {
    try {
      const response = await invitationService.getPendingInvitations();
      const invitations = response.invitations || [];
      setPendingInvitations(invitations);
      return invitations;
    } catch {
      return [];
    }
  };

  const refreshPendingInvitations = async () => {
    await checkPendingInvitations();
  };

  const resendMfaCode = async () => {
    if (!mfaChallenge) {
      throw new Error('No MFA challenge in progress');
    }
    await authService.resendMfaCode(mfaChallenge.mfaSessionId);
  };

  const verifyMfa = async (otp) => {
    if (!mfaChallenge) {
      throw new Error('No MFA challenge in progress');
    }

    try {
      const response = await authService.verifyMfa(mfaChallenge.mfaSessionId, otp);

      localStorage.setItem('idToken', response.idToken);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));

      setUser(response.user);
      setIdToken(response.idToken);
      setMfaChallenge(null);

      const pendingInvitations = await checkPendingInvitations();
      return { ...response.user, pendingInvitations };
    } catch (error) {
      console.error('MFA verification failed:', error);
      throw error;
    }
  };

  const verifyCognitoMfa = async (code) => {
    if (!cognitoMfaChallenge) throw new Error('No Cognito MFA challenge in progress');
    try {
      const response = await authService.respondCognitoChallenge(
        'EMAIL_OTP', cognitoMfaChallenge.username, cognitoMfaChallenge.session, code
      );
      localStorage.setItem('idToken', response.idToken);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
      setIdToken(response.idToken);
      setCognitoMfaChallenge(null);
      const pendingInvitations = await checkPendingInvitations();
      return { ...response.user, pendingInvitations };
    } catch (error) {
      console.error('Cognito MFA verification failed:', error);
      throw error;
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    if (!user?.username && !user?.email) {
      throw new Error('User not authenticated');
    }

    try {
      await authService.changePassword(oldPassword, newPassword);
      console.log('Password changed successfully');
    } catch (error) {
      console.error('Password change failed:', error);
      throw error;
    }
  };

  const isSuperAdmin = user?.isSuperAdmin === true;
  const isPartner = user?.isPartner === true;
  // A user can belong to MANY partners (resolved from the DB permissions junction).
  const partners = user?.partners || [];
  // Transitional single value (the backend sends the first partner). Prefer `solePartnerId`.
  const partnerId = user?.partnerId || null;
  // Only auto-resolve a single partner when it's unambiguous (exactly one). With more than one,
  // callers must use the explicitly selected partner — never assume "the" partner.
  const solePartnerId = partners.length === 1 ? partners[0].partnerId : null;
  // The vendor this person works for, if any: { credentialId, partnerId, name }. NOT a partner membership —
  // a person at a vendor reaches only the vendor's granted chains and hotels; this only says which partner
  // they are a vendor FOR (navbar) and which vendor's settings are theirs (Vendor Settings).
  const vendorOf = user?.vendorOf || null;

  const value = {
    user,
    idToken,
    loading,
    login,
    logout,
    changePassword,
    completeNewPassword,
    verifyMfa,
    resendMfaCode,
    verifyCognitoMfa,
    cognitoMfaChallenge,
    checkUser,
    isAuthenticated: !!user,
    isSuperAdmin,
    isPartner,
    partners,
    partnerId,
    solePartnerId,
    vendorOf,
    passwordChallenge,
    mfaChallenge,
    pendingInvitations,
    refreshPendingInvitations
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
